<template>
  <div
    :class="[
      'z-10 bg-surface-lowest flex flex-col',
      mobile ? 'gap-1.5 px-4 pt-2.5' : isShortViewport ? 'gap-2 px-6 pt-2' : 'gap-2 px-6 pt-4',
    ]"
  >
    <!-- Breadcrumb row: category path on the left, market status badge floated on the right.
         Only rendered when there is a breadcrumb OR a badge to show, so the row collapses
         cleanly for non-hourly products before the tree resolves. `min-h-4` keeps the row's
         height pinned to the breadcrumb's own line-height at all times — the badge is
         absolutely positioned (not a flex sibling) specifically so its own, taller box never
         grows the row and pushes the title/tabs below it down when it fades in/out.
         Each ancestor crumb links to the markets page filtered by that category; the leaf
         (current product category) is non-interactive — except the L1 crumb, which is always
         clickable so a product sitting directly under L1 (no L2) can still return to its L1
         contracts. -->
    <div v-if="electionBoardReturnUrl || displayedCrumbs.length > 0 || showStatusBadge" class="relative min-h-4">
      <!-- Right padding reserves the badge's lane. The badge is absolutely positioned (see
           above), so it is invisible to the breadcrumb's layout — without this the crumbs ran
           straight underneath it and the two texts overlapped into each other on narrow
           viewports. Padding rather than a flex sibling keeps the badge out of flow, so its
           taller box still cannot grow this row. -->
      <div v-if="electionBoardReturnUrl" :class="showStatusBadge && (mobile ? 'pr-30' : 'pr-60')">
        <button
          type="button"
          class="election-board-return group inline-flex max-w-full items-center gap-2 rounded-xl py-1.5 pl-1.5 pr-3 text-on-surface cursor-pointer shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-px hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent motion-reduce:transform-none motion-reduce:transition-none"
          :class="{ 'election-board-return--dark': isDark }"
          @click="returnToElectionBoard"
        >
          <span
            class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-surface-hover text-on-surface transition-transform duration-200 group-hover:-translate-x-0.5 motion-reduce:transform-none"
          >
            <ElectionBoardIcon class="size-[18px]" />
          </span>
          <span class="truncate text-xs font-bold leading-4">{{ $t('returnToElectionBoard') }}</span>
        </button>
      </div>
      <nav
        v-else-if="displayedCrumbs.length > 0"
        :class="[
          'flex items-baseline gap-2 text-[12px] leading-[16px] min-w-0 [word-break:break-word]',
          showStatusBadge && (mobile ? 'pr-30' : 'pr-60'),
        ]"
        :aria-label="$t('breadcrumbNavLabel')"
      >
        <template v-for="(crumb, position) in displayedCrumbs" :key="crumb.id">
          <span v-if="position > 0" class="text-on-surface-secondary shrink-0">/</span>
          <button
            v-if="crumb.interactive"
            type="button"
            class="!text-accent min-w-0 cursor-pointer hover:underline truncate"
            @click="goToCategory(crumb.depth)"
          >
            <!-- The back affordance on mobile: a collapsed trail reads as "up one level"
                 rather than as a chain, so it is prefixed with a caret. -->
            <span v-if="crumb.showBackAffordance" aria-hidden="true">&lsaquo;&nbsp;</span>{{ crumb.name }}
          </button>
          <span v-else class="text-on-surface-secondary min-w-0 truncate">{{ crumb.name }}</span>
        </template>
      </nav>
      <!-- Available for every selected measured period with market timing data. The badge
           handles the 24-hour opening and closing display windows. -->
      <Transition name="content-fade">
        <LiveStatusBadge
          v-if="showStatusBadge"
          :last-trade-time="productDetailsStore.selectedContractLastTradeTime"
          :start-time="productDetailsStore.selectedContractStartTime"
          class="absolute right-0 top-1/2 -translate-y-1/2"
          :compact="mobile"
        />
      </Transition>
    </div>

    <!-- Conditional subtitle: icon + the overall product name above the
         selected condition title, gated by the SHOW_PR_HUB_CONDITIONALS AB feature.
         Accent-colored on the white header surface (vs. white-on-image on the
         product cards). Falls back to the generic "Conditional" label until the
         product metadata resolves. Hover reveals the mandated description copy. -->
    <div
      v-if="showConditional"
      v-tooltip.bottom="{ value: $t('conditionalTooltip'), class: 'question-tooltip' }"
      class="flex items-center gap-1 -mb-1 cursor-help w-fit"
    >
      <ConditionalIcon class="size-[18px] text-accent shrink-0" />
      <span class="text-accent text-xs uppercase leading-4 font-semibold [word-break:break-word]">{{
        productName || $t('conditional')
      }}</span>
    </div>

    <!-- Title. Conditional products are grouped by their conditioning outcome, so the title
         switches between outcomes. For hourly products with a live expiration menu, the whole
         title acts as a dropdown trigger; otherwise it renders as static text. -->
    <div class="flex items-baseline gap-2 [word-break:break-word]">
      <template v-if="showConditionSelector">
        <button
          type="button"
          class="flex items-center gap-2 cursor-pointer text-left"
          :aria-label="$t('switchConditionalProduct')"
          :aria-haspopup="true"
          :aria-expanded="titleMenuOpen"
          @click="toggleTitleMenu"
        >
          <Transition name="contract-roll" mode="out-in">
            <span :key="selectedConditionConid" :class="titleFontClasses">{{ selectedConditionLabel }}</span>
          </Transition>
          <i class="ibi-caret-expand-down text-on-surface text-[16px]" />
        </button>
      </template>
      <Transition v-else name="content-fade" mode="out-in">
        <Skeleton v-if="!productName" key="skeleton" width="20rem" height="2rem" animation="none" />
        <button
          v-else-if="showTitleMenu"
          key="menu"
          type="button"
          class="flex items-center gap-2 cursor-pointer text-left"
          :aria-haspopup="true"
          :aria-expanded="titleMenuOpen"
          @click="toggleTitleMenu"
        >
          <Transition name="contract-roll" mode="out-in">
            <span :key="productDetailsStore.selectedExpiration" :class="titleFontClasses">
              {{ titleText }}
            </span>
          </Transition>
          <i class="ibi-caret-expand-down text-on-surface text-[16px]" />
        </button>
        <Transition v-else name="contract-roll" mode="out-in">
          <p :key="productDetailsStore.selectedExpiration || 'name'" :class="titleFontClasses">{{ titleText }}</p>
        </Transition>
      </Transition>
    </div>

    <Menu
      ref="titleMenuRef"
      :model="titleMenuItems"
      :popup="true"
      :pt="{ root: '!min-w-[20rem]' }"
      @show="titleMenuOpen = true"
      @hide="titleMenuOpen = false"
    >
      <template #item="{ item, props: itemProps }">
        <a
          v-bind="itemProps.action"
          class="relative flex min-h-12 items-center !px-4 !py-2"
          :style="{
            color: isSelectedMenuItem(item) ? 'var(--p-primary-color, #0060df)' : undefined,
            transition: 'color 180ms ease',
          }"
        >
          <span
            aria-hidden="true"
            :style="{
              position: 'absolute',
              left: '0',
              top: '6px',
              bottom: '6px',
              width: '4px',
              borderRadius: '0 2px 2px 0',
              backgroundColor: 'var(--p-primary-color, #0060df)',
              opacity: isSelectedMenuItem(item) ? '1' : '0',
              transform: isSelectedMenuItem(item) ? 'scaleY(1)' : 'scaleY(0.35)',
              transformOrigin: 'center',
              transition: 'opacity 180ms ease, transform 240ms cubic-bezier(0.22, 1, 0.36, 1)',
            }"
          />
          <span :style="{ color: isSelectedMenuItem(item) ? 'var(--p-primary-color, #0060df)' : undefined }">
            {{ item.label }}
          </span>
        </a>
      </template>
    </Menu>

    <!-- Desktop adds a little margin on top of the root gap to separate the tab strip from
         the title block; mobile and short screens rely on the compact tabs' own top padding. -->
    <TabNavigation :tabs="tabs" :compact="compactTabs" :class="{ 'mt-1': !compactTabs }" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import Skeleton from 'primevue/skeleton';
import { useI18n } from 'vue-i18n';
import { media } from '@ibgroup-tws-webapps/ratio.ui';
import { useProductDetails } from '@/stores/useProductDetails';
import { useUserState } from '@/stores/useUserState';
import { useGetCategoryPath } from '@/composables/useCategories';
import { useNavigation } from '@/composables/useNavigation';
import { useConditionals } from '@/composables/useConditionals';
import { useDismissFlag } from '@/composables/useDismissFlag';
import ConditionalIcon from '@/icons/ConditionalIcon.vue';
import TabNavigation from './TabNavigation.vue';
import LiveStatusBadge from './LiveStatusBadge.vue';
import { buildElectionBoardReturnUrl } from '@/electionHub/utils/predictionMarketsNavigation';
import ElectionBoardIcon from '@/icons/ElectionBoardIcon.vue';

const props = withDefaults(defineProps<{ mobile?: boolean }>(), { mobile: false });

// Wide-but-short screens (an unfolded iPhone Duo) get the desktop header, but every pixel of
// height there decides how much chart and how many contracts fit. Same cutoff as the split
// layout in ProductContracts.
const isShortViewport = useMediaQuery('(max-height: 640px)');
const compactTabs = computed(() => props.mobile || isShortViewport.value);

const { t } = useI18n();
const isDark = computed(() => media.scheme === 'dark');
const productDetailsStore = useProductDetails();
const productName = computed(() => productDetailsStore.productName);
const showStatusBadge = computed(() => productDetailsStore.selectedContractLastTradeTime > 0);

const { isConditionalsEnabled } = useConditionals();
const showConditional = computed(() => isConditionalsEnabled.value && productDetailsStore.isConditional);

const { data: categoryPath } = useGetCategoryPath(() => productDetailsStore.categoryId);

/**
 * Breadcrumbs to render, with each crumb's index into the full path preserved.
 *
 * Mobile collapses the trail to the immediate parent instead of the whole L1/L2/L3 chain:
 * three crumbs plus separators cannot fit beside the status badge on a phone, so they wrapped
 * and truncated into something unreadable. One parent crumb reads as "go up a level", which is
 * what the trail is actually for at that width; the full ancestry stays on wide viewports.
 *
 * `depth` is carried explicitly rather than relying on the render index, because
 * `goToCategory` indexes into the *full* path — re-indexing a sliced array would silently
 * link every collapsed crumb to the wrong category.
 */
interface DisplayCrumb {
  id: string;
  name: string;
  /** Index into the full categoryPath, for goToCategory. */
  depth: number;
  interactive: boolean;
  showBackAffordance: boolean;
}

const displayedCrumbs = computed<DisplayCrumb[]>(() => {
  const path = categoryPath.value ?? [];
  if (!path.length) return [];

  const toCrumb = (depth: number, overrides: Partial<DisplayCrumb> = {}): DisplayCrumb => ({
    id: path[depth].id,
    name: path[depth].name,
    depth,
    // The leaf is the current product's own category — not somewhere to navigate. The L1
    // crumb stays clickable even when it is also the leaf, so a product sitting directly
    // under L1 can still get back to its L1 contracts.
    interactive: depth === 0 || depth < path.length - 1,
    showBackAffordance: false,
    ...overrides,
  });

  if (!props.mobile) return path.map((_, depth) => toCrumb(depth));

  // Single crumb pointing one level up. The leaf itself is dropped: the product title
  // directly beneath already names where you are.
  const parentDepth = Math.max(0, path.length - 2);
  return [toCrumb(parentDepth, { interactive: true, showBackAffordance: true })];
});

const conditionalGroups = computed(() => productDetailsStore.conditionalGroups ?? []);
const selectedConditionConid = computed(() => productDetailsStore.selectedConditionConid ?? 0);
const selectedConditionLabel = computed(
  () =>
    conditionalGroups.value.find((condition) => condition.conid === selectedConditionConid.value)?.label ??
    productName.value,
);
const showConditionSelector = computed(() => showConditional.value && conditionalGroups.value.length > 1);

// selectedExpiration + expirationOptions live in the pinia store because the header and
// ProductContracts are sibling children of ProductDetails — provide/inject can't flow
// between siblings. ProductContracts publishes options + the current selection; this
// component reads them and writes selection changes back through the store setter.
const showTitleMenu = computed(
  () => productDetailsStore.hasIntradayContracts && productDetailsStore.expirationOptions.length > 1,
);

interface ExpirationOption {
  label: string;
  value: string;
}

const selectedOption = computed<ExpirationOption | undefined>(() =>
  productDetailsStore.expirationOptions.find((o) => o.value === productDetailsStore.selectedExpiration),
);

const titleText = computed(() => {
  const name = productName.value;
  if (!productDetailsStore.hasIntradayContracts || !selectedOption.value) return name;
  return `${name} ${selectedOption.value.label}`;
});

// Mobile scales with the viewport (16px on a 320px phone up to 20px on large phones):
// long intraday titles like "Bitcoin Price Up Down Oct02'26 10:00 AM ET (15Min)" otherwise
// wrap at a fixed 20px and push the first contract row below the fold.
const titleFontClasses = computed(() =>
  props.mobile
    ? 'font-semibold text-on-surface text-[length:clamp(1rem,4.6vw,1.25rem)] leading-[1.3]'
    : 'font-semibold text-on-surface text-[24px] leading-[30px]',
);

// PrimeVue Menu items — one per expiration option. Selection routes through the store
// setter so ProductContracts.vue's own watchers keep firing (badge sync, deep-link
// resolution, etc.). Closed options render dimmed and the currently-selected option
// gets the highlight class via `itemOptions`.
interface TitleMenuItem extends MenuItem {
  label: string;
  value: string | number;
  selected: boolean;
  command: () => void;
  class: string;
}

function isClosedLabel(label: string): boolean {
  return label.includes(`(${t('closed')})`);
}

function isSelectedMenuItem(item: MenuItem): boolean {
  return item.selected === true;
}

const titleMenuItems = computed<TitleMenuItem[]>(() => {
  if (showConditionSelector.value) {
    return conditionalGroups.value.map((condition) => ({
      label: condition.label,
      value: condition.conid,
      selected: condition.conid === selectedConditionConid.value,
      class: '',
      command: () => productDetailsStore.setSelectedConditionConid(condition.conid),
    }));
  }

  return productDetailsStore.expirationOptions.map((opt) => {
    const classes: string[] = [];
    if (isClosedLabel(opt.label)) classes.push('opacity-60');
    return {
      label: `${productName.value} ${opt.label}`,
      value: opt.value,
      selected: opt.value === productDetailsStore.selectedExpiration,
      class: classes.join(' '),
      command: () => productDetailsStore.setSelectedExpiration(opt.value),
    };
  });
});

const titleMenuRef = ref<InstanceType<typeof Menu> | null>(null);
const titleMenuOpen = ref(false);
const route = useRoute();
const electionBoardReturnUrl = computed(() =>
  buildElectionBoardReturnUrl(
    route.query as Record<string, unknown>,
    (route.params.productConid as string | undefined) || productDetailsStore.underlyingConid,
  ),
);

function closeTitleMenu() {
  titleMenuRef.value?.hide();
  titleMenuOpen.value = false;
}

watch(() => route.fullPath, closeTitleMenu);

function toggleTitleMenu(event: Event) {
  titleMenuRef.value?.toggle(event);
}

const { openUrl } = useNavigation();

function returnToElectionBoard() {
  if (!electionBoardReturnUrl.value) return;
  closeTitleMenu();
  openUrl(electionBoardReturnUrl.value);
}

const goToCategory = (depth: number) => {
  const path = categoryPath.value;
  const crumb = path[depth];
  if (!crumb) return;
  closeTitleMenu();

  // Mobile renders the dedicated /category/:id route (CategoryWrapper), which
  // takes a single category id. Deep-link to the clicked crumb's own category.
  if (props.mobile) {
    openUrl(`tws://forecast_trader2/category/${crumb.id}`);
    return;
  }

  // Wide viewport: deep-link to the markets page filtered to the clicked level.
  // The markets query params are positional: category = L1 (path[0]), sub = L2
  // (path[1]), filters = L3 (path[2+]). Clicking an ancestor at `depth` carries
  // every level from the root down to it, so e.g. an L2 crumb yields
  // ?category=<L1>&sub=<L2>. openUrl resolves the tws:// module URL internally
  // when the markets route exists, else hands off to the host's hash router.
  const l1 = path[0]?.id;
  if (!l1) return;

  const params = new URLSearchParams({ category: l1 });
  if (depth >= 1 && path[1]) params.set('sub', path[1].id);
  if (depth >= 2) {
    const filters = path.slice(2, depth + 1).map((c) => c.id);
    if (filters.length > 0) params.set('filters', filters.join(','));
  }

  openUrl(`tws://forecast_trader2/?${params.toString()}`);
};

const userState = useUserState();
const isEnvironmental = computed(() => categoryPath.value[0]?.id === 'g137800');
const { isActive: showToolsResearchBadge, markDone: markToolsResearchSeen } =
  useDismissFlag('tools-research-badge-seen');

const openEnvironmentalResearch = () => {
  markToolsResearchSeen();
  openUrl(`https://weather.weatherclimatehumansystems.org/${productDetailsStore.categoryId}.html`, {
    target: '_blank',
    external: true,
  });
};

const tabs = computed(() => {
  const list = [
    { name: 'ProductContracts', label: 'Contracts' },
    { name: 'ProductRelatedContracts', label: props.mobile ? 'Related' : 'Related Products' },
  ];
  if (categoryPath.value.length > 0 && (isEnvironmental.value || !productDetailsStore.hasIntradayContracts)) {
    const researchTab = isEnvironmental.value
      ? {
          name: 'ProductResearch',
          label: t('toolsResearchTab'),
          command: openEnvironmentalResearch,
          newBadge: showToolsResearchBadge.value,
        }
      : { name: 'ProductResearch', label: 'Research' };
    list.push(researchTab);
  }
  if (userState.authenticated) {
    list.push({ name: 'ProductNews', label: 'News' });
  }
  return list;
});
</script>

<style scoped>
.election-board-return {
  border: 2px solid transparent;
  background:
    linear-gradient(var(--p-content-background), var(--p-content-background)) padding-box,
    linear-gradient(135deg, var(--color-republican-light), var(--color-democrat-light)) border-box;
}

.election-board-return--dark {
  background:
    linear-gradient(var(--p-content-background), var(--p-content-background)) padding-box,
    linear-gradient(135deg, var(--color-republican), var(--color-democrat)) border-box;
}

.contract-roll-enter-active,
.contract-roll-leave-active {
  transition:
    opacity 180ms ease,
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.contract-roll-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.contract-roll-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .contract-roll-enter-active,
  .contract-roll-leave-active {
    transition: none;
  }
}
</style>
<!-- last modified by AI tools on 2026-10-12 -->
